import type { AIProvider, AISession } from "../ai";
import type {
  DigestItem,
  DigestModel,
  TriageCategory,
  TriageResult,
  TriageUrgency,
} from "../storage-schema";
import type { ClassifiableItem } from "./gather";
import { sanitizeField } from "./sanitize";

export interface ClassifiedItem {
  item: ClassifiableItem;
  triage: TriageResult;
}

const NEEDS_CAP = 7;
// Exhaustive for the Record type; only critical/high are ranked at runtime (the rest never reach needsYou's sort).
const RANK: Record<TriageUrgency, number> = { critical: 0, high: 1, medium: 2, low: 3, noise: 4 };
const isHighStakes = (u: TriageUrgency) => u === "critical" || u === "high";

function toDigestItem({ item, triage }: ClassifiedItem): DigestItem {
  return {
    id: item.id,
    msgId: item.msgId,
    conversationKey: item.conversationKey,
    appPackage: item.appPackage,
    app: item.app,
    urgency: triage.urgency,
    category: triage.category,
    suggestedAction: triage.suggestedAction,
    oneLiner: triage.oneLiner,
    repliable: item.repliable,
  };
}

/** Pure: partition into needsYou (criticals never dropped — sorted first; the effective limit raises to fit all criticals, so only `high` overflows) + a quiet tally; `lead` is null — the caller fills it from narrateLead's stream. */
export function buildDigestModel(
  classified: ClassifiedItem[],
  scanned: number,
  pending: number
): DigestModel {
  const high = classified
    .filter((c) => isHighStakes(c.triage.urgency))
    .sort(
      (a, b) =>
        RANK[a.triage.urgency] - RANK[b.triage.urgency] ||
        b.item.input.timestamp - a.item.input.timestamp
    );
  const criticalCount = high.filter((c) => c.triage.urgency === "critical").length;
  const limit = Math.max(NEEDS_CAP, criticalCount); // never drop a critical
  const capped = high.slice(0, limit);

  const counts = new Map<TriageCategory, number>();
  for (const c of classified) {
    if (isHighStakes(c.triage.urgency)) continue;
    counts.set(c.triage.category, (counts.get(c.triage.category) ?? 0) + 1);
  }

  return {
    lead: null,
    needsYou: capped.map(toDigestItem),
    overflow: high.length - capped.length,
    quiet: [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([category, count]) => ({ category, count })),
    scanned,
    pending,
  };
}

function buildLeadPrompt(model: DigestModel): string {
  const needs =
    model.needsYou
      .map((i) => `- ${sanitizeField(i.app)}: ${sanitizeField(i.oneLiner)}`)
      .join("\n") || "(nothing urgent)";
  const quiet = model.quiet.map((q) => `${q.category} ${q.count}`).join(", ") || "none";
  return [
    "You write a one-line catch-up for someone returning to their phone notifications.",
    "Write ONE or TWO short sentences that name the concrete stakes/asks, most important first.",
    "Synthesize — do not list. Name the app or person when it helps. No preamble, no quotes.",
    "If nothing is urgent, calmly summarize the quiet items in one short sentence.",
    "",
    "NEEDS ATTENTION",
    needs,
    `QUIET: ${quiet}`,
    "",
    "Catch-up:",
  ].join("\n");
}

/** Stream the AI lead — each yield is the full running text (replace-semantics); a throw is the caller's to degrade. */
export async function* narrateLead(
  provider: AIProvider,
  model: DigestModel,
  opts?: { signal?: AbortSignal; outputLanguage?: string }
): AsyncIterable<string> {
  let session: AISession | undefined;
  try {
    session = await provider.createSession({
      temperature: 0.3,
      topK: 3,
      expectedOutputs: [{ type: "text", languages: [opts?.outputLanguage ?? "en"] }],
      signal: opts?.signal,
    });
    let acc = "";
    for await (const chunk of session.promptStreaming(buildLeadPrompt(model), {
      signal: opts?.signal,
    })) {
      // Belt-and-suspenders: promptStreaming already holds the signal for native cancel; this drops a post-abort delta.
      if (opts?.signal?.aborted) return;
      acc += chunk;
      yield acc;
    }
  } catch (err) {
    // Abort is a clean stop, not an error — swallow it; re-throw anything real for the caller to degrade.
    if (!opts?.signal?.aborted) throw err;
  } finally {
    session?.destroy();
  }
}
